import { Injectable } from "@angular/core";
import { BehaviorSubject } from "rxjs";
import { MapStatus } from "../../model/map-status";
import { Viewerlayer } from "../../model/viewerlayer";
import { ProjectStatus } from "../../model/project-status";
import { IPopup } from "../../interfaces/i-popup";

@Injectable({
  providedIn: "root",
})
export class DualCommunicatorService {
  private dualEnabledSubject = new BehaviorSubject<boolean>(false);

  public dualEnabled$ = this.dualEnabledSubject.asObservable();

  private enableSyncSubject = new BehaviorSubject<boolean>(false);

  public enableSync$ = this.enableSyncSubject.asObservable();

  private userLayersSubject = new BehaviorSubject<Viewerlayer[]>([]);

  public userLayers$ = this.userLayersSubject.asObservable();

  private allowSyncSubject = new BehaviorSubject<boolean>(true);

  public allowSync$ = this.allowSyncSubject.asObservable();

  private mapStatusSubject = new BehaviorSubject<MapStatus>(null);

  public mapStatus$ = this.mapStatusSubject.asObservable();

  public unitSelectedSubject = new BehaviorSubject<string>("metric");

  public unitSelected$ = this.unitSelectedSubject.asObservable();

  startMapStatus: MapStatus;

  startProjectStatus: ProjectStatus;

  startBaseLayers: Viewerlayer[];

  startSelectedBaseLayer: Viewerlayer;

  startOverlayLayers: Viewerlayer[];

  startDiscreteEnabled: boolean;

  startOrientationSelected: string;

  startEnabledTool: string;

  startInfoProperties: IPopup[];

  syncMaps: Map<string, any> = new Map<string, any>();

  mapStatus: Map<string, any> = new Map<string, any>();

  isDualEnabled() {
    return this.dualEnabledSubject.getValue();
  }

  isSyncEnabled() {
    return this.enableSyncSubject.getValue();
  }

  /**
   * Checks if the dual view synchronization is allowed.
   * returns False if additional images mode is enabled
   */
  isSyncAllowed(): boolean {
    return this.allowSyncSubject.getValue();
  }

  addMap(id: string, map: any) {
    this.syncMaps.set(id, map);
  }

  removeMap(id: string) {
    if (id) {
      this.syncMaps.delete(id);
    }
    if (this.syncMaps.size === 1) {
      this.dualEnabledSubject.next(false);
    }
  }

  updateDualStatus(enableDual: boolean) {
    this.dualEnabledSubject.next(enableDual);

    if (enableDual) {
      this.synchronizeStatus();
    } else {
      this.desynchronizeStatus();
    }
  }

  updateEnableSync(enableSync: boolean) {
    this.enableSyncSubject.next(enableSync);
    if (enableSync) {
      this.synchronizeStatus();
    } else {
      this.desynchronizeStatus();
    }
  }

  updateAllowSync() {
    let allowSync = true;
    this.syncMaps.forEach((rootMap) => {
      if (rootMap.checkLockedDiscreteMode()) {
        allowSync = false;
      }
    });
    this.allowSyncSubject.next(allowSync);
    if (this.dualEnabledSubject.getValue() && allowSync) {
      this.synchronizeStatus();
    } else {
      this.desynchronizeStatus();
    }
  }

  /**
   * Enable synchronize and update location
   */
  synchronizeStatus() {
    const rootMap = this.syncMaps.values().next().value;
    if (this.syncMaps.size > 1 && this.isDualEnabled() && this.isSyncEnabled() && this.isSyncAllowed()) {
      const children = Array.from(this.syncMaps.values()).slice(1, this.syncMaps.size);
      rootMap.synchronize(children, true);
    }
  }

  desynchronizeStatus() {
    const rootMap = this.syncMaps.values().next().value;
    if (!!this.syncMaps.size && !this.isDualEnabled()) {
      rootMap.deSynchronize(true);
    }
  }

  updateStartConfiguration(
    startMapStatus: MapStatus,
    startProjectStatus: ProjectStatus,
    startBaseLayers: Viewerlayer[],
    startSelectedBaseLayer: Viewerlayer,
    startOverlayLayers: Viewerlayer[],
    startDiscreteEnabled: boolean,
    startOrientationSelected: string,
    startEnabledTool: string,
    startInfoProperties: IPopup[],
  ) {
    this.startMapStatus = startMapStatus;
    this.startProjectStatus = startProjectStatus;
    this.startBaseLayers = startBaseLayers;
    this.startSelectedBaseLayer = startSelectedBaseLayer;
    this.startOverlayLayers = startOverlayLayers;
    this.startDiscreteEnabled = startDiscreteEnabled;
    this.startOrientationSelected = startOrientationSelected;
    this.startEnabledTool = startEnabledTool;
    this.startInfoProperties = startInfoProperties;
  }

  getUserLayers(): Viewerlayer[] {
    return this.userLayersSubject.getValue();
  }

  updateUserLayers(layers: Viewerlayer[]) {
    this.userLayersSubject.next(layers);
  }

  changeUnitSelected(unit: string) {
    this.unitSelectedSubject.next(unit);
  }
}
